'use client';

import { Input } from '@material-tailwind/react';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import {
  createValidityState,
  InputValidityState,
  useInputValidation,
  ValidationHandler,
} from 'hooks/forms';
import {
  ComponentProps,
  forwardRef,
  useEffect,
  useRef,
  useState,
  JSX,
} from 'react';

type TextInputValidityState = InputValidityState<{
  patternMismatch: boolean;
  tooLong: boolean;
  tooShort: boolean;
  valueMissing: boolean;
}>;

export function BaseAlphaTextInput(
  {
    containerClassName,
    prefixClassName,
    onValidate,
    hideValidation,
    indeterminate,
    setCustomValidity,
    onChange,
    ...props
  }: Omit<
    ComponentProps<typeof Input>,
    'defaultValue' | 'value' | 'onChange'
  > & {
    containerClassName?: string;
    prefixClassName?: string;
    onValidate?: ValidationHandler<string | number, TextInputValidityState>;
    defaultValue?: string | number;
    hideValidation?: boolean;
    indeterminate?: boolean;
    setCustomValidity?: (formValue: string | number) => string;
    value?: string | number;
    onChange?:
      | ((
          event: React.ChangeEvent<HTMLInputElement>,
          validity: TextInputValidityState,
        ) => void)
      | undefined;
    prefix?: string;
    suffix?: string;
  },
  ref: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const [rawValue, setRawValue] = useState(props.value ?? props.defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const combinedRef = (ref as React.RefObject<HTMLInputElement>) || inputRef;

  const pattern =
    props.pattern ?? (props.type === 'email' ? '.+@.+' : undefined);
  const formValue = rawValue ?? '';
  const customValidity = setCustomValidity?.(formValue) || '';

  const validity = createValidityState({
    customError: Boolean(customValidity),
    patternMismatch: Boolean(
      pattern != null &&
        !new RegExp(`^${pattern}$`, 'u').test(formValue.toString()),
    ),
    tooLong: Boolean(
      props.maxLength && formValue.toString().length > props.maxLength,
    ),
    tooShort: Boolean(
      props.minLength && formValue.toString().length < props.minLength,
    ),
    valueMissing: Boolean(props.required && !rawValue),
  });

  const message = onValidate?.({ value: rawValue ?? '', validity }) ?? '';

  // Always set the validity message in case the user submits a form without interacting with the
  // input first.
  useEffect(
    () => combinedRef.current?.setCustomValidity(message || customValidity),
    [message, customValidity, combinedRef],
  );

  // Update the raw value when the props value changes
  // This is to ensure the raw value is always in sync with the props value
  // Example case is updating value directly via state which wont trigger onInput
  // Should only happen on controlled component, hence the check for both onChange and value props
  useEffect(() => {
    if ((props?.value || props?.value === '') && onChange) {
      setRawValue(props?.value);
    }
  }, [props?.value, onChange]);

  const state = useInputValidation({
    indeterminate: indeterminate ? indeterminate : rawValue == null,
    validity,
    validate: () => message || customValidity,
  });

  return (
    <div className={classNames(containerClassName)}>
      <div className="relative">
        {props.prefix && (!!rawValue || state.hasFocus) && (
          <span
            className={
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              classNames(
                'mhg-alpha-form-label pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 font-[--font-weight-form-input] text-[--font-size-form-input]',
                prefixClassName,
              )
            }
          >
            {props.prefix}
          </span>
        )}
        <Input
          icon={
            state.hasFocus ? null : state.valid === true && rawValue ? (
              <MaterialIcon
                icon="check_circle"
                className="text-highlights/green-dark"
              />
            ) : state.valid === false ? (
              <MaterialIcon
                icon="cancel"
                className="text-highlights/red-dark"
              />
            ) : null
          }
          {...props}
          inputRef={combinedRef}
          onFocus={(e) => {
            state.setFocus(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            state.setFocus(false);
            props.onBlur?.(e);
          }}
          onInput={(event) => {
            const isControlled =
              (props?.value || props?.value === '') && onChange;

            // For controlled components, we handle the raw value change using useEffect above
            if (!isControlled) {
              setRawValue(event.currentTarget.value);
            }
          }}
          onInvalid={(event) => {
            setRawValue(event.currentTarget.value);
            state.setFocus(false); // todo: force update instead
          }}
          onChange={(event) => {
            onChange?.(event, validity);
          }}
          error={props.error ?? state.valid === false}
          success={props.success ?? state.valid === true}
          // Add padding-left to avoid overlap with prefix
          className={classNames(
            props.className,
            props.prefix && (!!rawValue || state.hasFocus) ? '!pl-10' : '',
          )}
          placeholder={props.prefix && state.hasFocus ? '' : props.placeholder}
        />
        {props.suffix && !!rawValue && (
          <span
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            className="mhg-alpha-form-label absolute top-1/2 -translate-y-1/2 font-[--font-weight-form-input] text-[--font-size-form-input]"
            style={{
              left: rawValue
                ? `calc(${rawValue.toString().length}ch + 1rem)`
                : '1rem',
            }}
          >
            {props.suffix ?? ''}
          </span>
        )}
      </div>
      {hideValidation !== true && state.errorMessage && (
        <div className="px-padding/x-sm pt-1 text-highlights/red-dark alpha-caption">
          {state.errorMessage}
        </div>
      )}
    </div>
  );
}

export const AlphaTextInput = forwardRef(BaseAlphaTextInput);

AlphaTextInput.displayName = 'AlphaTextInput';
